import React, {useState, useCallback} from 'react';
import {Iterable} from 'immutable';
import classnames from 'classnames';
import {Badge, IconButton, Menu, RangeInput, Text} from '@albert-io/atomic';
import {getMarkdownRenderer} from 'generic/MarkdownRendererV2/markdownRendererRef';
import './passage.scss';
import {
  NoTranslationWarningHeader,
  notAvailableText
} from 'client/components/QuestionTypes/shared/TranslationSection';
import {FONT_SIZES} from '@albert-io/atomic/atoms/Text/Text.react';
import CustomScrollbar from './CustomScrollbar';
import {noop} from 'lodash';
import classNames from 'classnames';

interface Props {
  content: any;
  noTranslation?: boolean;
  showTranslationInfo?: boolean;
  showTranslatedSupplements?: boolean;
  showBorders?: boolean;
  showHeader?: boolean;
  forceMaxHeight?: boolean;
  isSmall?: boolean;
  className?: string;
}

interface MakeLineProps {
  hideLineNumbers: boolean;
  excludeWhiteSpace: boolean;
  lineNumberAlignment?: string;
  excludeHeaders?: boolean;
  lineNumberIncrement?: number;
}

// makes it easier for the range input, since FONT_SIZES is ordered largest to smallest
const reversedFontSizes = FONT_SIZES.slice().reverse();
const FONT_SIZE_IDX_REGULAR = reversedFontSizes.indexOf('regular');

const Passage = ({
  content,
  noTranslation = false,
  showTranslatedSupplements = false,
  showTranslationInfo = false,
  showBorders = true,
  showHeader = true,
  forceMaxHeight = false,
  isSmall = false,
  className: classNameProp
}: Props) => {
  const [fontSizeIndex, setFontSizeIndex] = useState(FONT_SIZE_IDX_REGULAR);
  const [showLines, setShowLines] = useState(true);

  const getContent = useCallback(() => {
    return Iterable.isIterable(content) ? content.toJS() : content;
  }, [content]);

  const hasContent = useCallback((line) => {
    return /[^\s]/.test(line);
  }, []);

  const getFormattedLines = useCallback(
    (text: string, ignoreWhiteSpace: boolean) => {
      if (text) {
        const splitText = text.split(/[\n\r]/);
        return ignoreWhiteSpace ? splitText.filter(hasContent) : splitText;
      }
      return null;
    },
    [hasContent]
  );

  const makeLines = useCallback(
    (lines: string[] | null, lineProps: MakeLineProps) => {
      const {
        hideLineNumbers,
        excludeWhiteSpace,
        lineNumberAlignment,
        excludeHeaders,
        lineNumberIncrement
      } = lineProps;

      let wasPrevLineEmpty = false;
      let lineNumber = 0;
      if (lines) {
        return lines.map((line, i) => {
          const isEmptyLine = !hasContent(line);
          const isHeaderLine = line.charAt(0) === '#';
          if (wasPrevLineEmpty && isEmptyLine) {
            return null;
          }
          wasPrevLineEmpty = isEmptyLine;
          if (!excludeWhiteSpace || !isEmptyLine) {
            if (!excludeHeaders || !isHeaderLine) {
              lineNumber += 1;
            }
          }

          return (
            <PassageLine
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              hideLineNumbers={hideLineNumbers}
              lineNumber={
                excludeWhiteSpace && isEmptyLine
                  ? null
                  : isHeaderLine && excludeHeaders
                    ? null
                    : lineNumber
              }
              line={isEmptyLine ? '\u00a0' : line}
              fontSize={reversedFontSizes[fontSizeIndex]}
              lineNumberAlignment={lineNumberAlignment}
              lineNumberIncrement={lineNumberIncrement}
            />
          );
        });
      }
      return null;
    },
    [hasContent, fontSizeIndex]
  );

  const {
    excludeWhiteSpace,
    hideLineNumbers,
    ignoreWhiteSpace,
    text,
    lineNumberAlignment,
    lineNumberIncrement,
    excludeHeaders,
    setMaxHeight,
    title
  } = getContent();
  const lines = getFormattedLines(text, ignoreWhiteSpace);

  const showWarning = (noTranslation || !showTranslatedSupplements) && showTranslationInfo;

  const lineProps = {
    hideLineNumbers,
    excludeWhiteSpace,
    lineNumberAlignment,
    excludeHeaders,
    lineNumberIncrement
  };

  const listClassNames = classNames('m-x0 px-x2', {
    'min-h-full': setMaxHeight || forceMaxHeight,
    'h-full py-x1-half': !(setMaxHeight || forceMaxHeight),
    'px-x1!': isSmall
  });

  const passageContent = (
    <>
      {showHeader && (
        <div
          className={classNames('flex items-center justify-between border-border-primary', {
            'border-b': showLines
          })}
        >
          <div className='min-w-0 flex-1 truncate px-x2 py-x1-half font-bold'>
            {title || (showLines ? <>&nbsp;</> : 'Passage')}
          </div>
          <div
            className='flex items-center justify-between pr-x2'
            role='presentation'
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {showLines && (
              <Menu position='bottom-end'>
                <Menu.Trigger>
                  {(triggerProps) => (
                    <IconButton
                      {...triggerProps}
                      color='secondary'
                      variant='text'
                      size='s'
                      icon='text-size'
                      label=''
                      iconStyle='regular'
                      tooltip='Text size'
                    />
                  )}
                </Menu.Trigger>
                <Menu.Content>
                  <Menu.Item
                    disableHover
                    disableActive
                    className='flex items-center justify-center p-x1'
                  >
                    <IconButton
                      icon='font'
                      iconStyle='regular'
                      label=''
                      size='s'
                      disabled={fontSizeIndex === 0}
                      onClick={() => setFontSizeIndex(fontSizeIndex - 1)}
                      className='passage-supplement__range-down'
                    />
                    <RangeInput
                      label='Range Input'
                      min={0}
                      max={FONT_SIZES.length - 1}
                      value={fontSizeIndex}
                      onChange={(e) => setFontSizeIndex(Number(e.target.value))}
                      className='flex'
                    />
                    <IconButton
                      icon='font'
                      size='s'
                      iconStyle='regular'
                      label=''
                      className='passage-supplement__range-up'
                      disabled={fontSizeIndex === FONT_SIZES.length - 1}
                      onClick={() => setFontSizeIndex(fontSizeIndex + 1)}
                    />
                  </Menu.Item>
                  <Menu.Item disableHover disableActive className='flex justify-center p-x1'>
                    <Text
                      className='cursor-pointer'
                      onClick={() =>
                        fontSizeIndex !== FONT_SIZE_IDX_REGULAR
                          ? setFontSizeIndex(FONT_SIZE_IDX_REGULAR)
                          : noop
                      }
                      size='s'
                      disabled={fontSizeIndex === FONT_SIZE_IDX_REGULAR}
                    >
                      Reset size
                    </Text>
                  </Menu.Item>
                </Menu.Content>
              </Menu>
            )}
            {showBorders && (
              <IconButton
                color='secondary'
                variant='text'
                size='s'
                icon={showLines ? 'arrows-to-line' : 'arrows-from-line'}
                label=''
                iconStyle='regular'
                onClick={() => setShowLines(!showLines)}
                tooltip={showLines ? 'Hide passage' : 'Show passage'}
              />
            )}
          </div>
        </div>
      )}
      {showLines && (
        <>
          {setMaxHeight || forceMaxHeight ? (
            <CustomScrollbar className='passage-supplement__scrollable' isSmall={isSmall}>
              <ul className={listClassNames}>{makeLines(lines, lineProps)}</ul>
            </CustomScrollbar>
          ) : (
            <ul className={listClassNames}>{makeLines(lines, lineProps)}</ul>
          )}
        </>
      )}
    </>
  );

  return showWarning ? (
    <NoTranslationWarningHeader text={notAvailableText} supplement>
      {passageContent}
    </NoTranslationWarningHeader>
  ) : (
    <div
      className={classNames(
        'rounded-2xl border-border-primary',
        {
          'mx-auto border': showBorders,
          'border-b': !showBorders && (setMaxHeight || forceMaxHeight) && showLines
        },
        classNameProp
      )}
    >
      {passageContent}
    </div>
  );
};

export default Passage;

interface PassageLineProps {
  hideLineNumbers: boolean;
  lineNumber: number | null;
  line: string;
  fontSize?: (typeof FONT_SIZES)[number];
  lineNumberAlignment?: string;
  lineNumberIncrement?: number;
}

const PassageLine = ({
  hideLineNumbers,
  lineNumber,
  line,
  fontSize,
  lineNumberAlignment,
  lineNumberIncrement
}: PassageLineProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const shouldShowLineNumber =
    !hideLineNumbers &&
    lineNumber !== null &&
    (lineNumberIncrement == null || lineNumber % lineNumberIncrement === 0 || isHovered);

  const shouldReserveSpace = !hideLineNumbers;

  const MarkdownRendererV2 = getMarkdownRenderer();

  return (
    <li
      className={classnames('passage-supplement-line--new', {
        'passage-line__badge-left':
          shouldReserveSpace && (lineNumberAlignment === 'left' || !lineNumberAlignment),
        'passage-line__badge-right': shouldReserveSpace && lineNumberAlignment === 'right'
      })}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {shouldReserveSpace && (lineNumberAlignment === 'left' || !lineNumberAlignment) && (
        <Badge
          className={classnames('passage-supplement-line__number--new mr-x1', {
            invisible: !shouldShowLineNumber
          })}
          status='neutral'
          size='small'
        >
          {lineNumber}
        </Badge>
      )}
      <Text size={fontSize} className='w-full leading-x3'>
        <MarkdownRendererV2 text={line} />
      </Text>
      {shouldReserveSpace && lineNumberAlignment === 'right' && (
        <Badge
          className={classnames('passage-supplement-line__number--new ml-x1', {
            invisible: !shouldShowLineNumber
          })}
          status='neutral'
          size='small'
        >
          {lineNumber}
        </Badge>
      )}
    </li>
  );
};
